import type { FeedEvent } from "../auth/api";

/**
 * Profit-and-loss helpers shared by the profile page (full history table +
 * chart) and the wallet card (7-day delta). Both need the same non-trivial
 * pairing of indexer events into per-match outcomes, so it lives here once.
 */

export interface MatchEntry {
  matchId: string;
  ts: number;
  blockNumber: number;
  txHash: string;
  outcome: "win" | "loss" | "draw";
  /** Profit/loss for this match, USDT/USDC base units (6 decimals). */
  pl: bigint;
  stake: bigint;
  /** Which game produced this match (affects volume + display). */
  source: "pong" | "fps" | "snake" | "td" | "math";
}

/** Map the API's `data.game` tag to a source. Falls back to "snake" for
 *  legacy/untagged HupperGames sessions. */
export function gameTagToSource(tag: unknown): MatchEntry["source"] {
  return tag === "fps" || tag === "td" || tag === "math" || tag === "snake" || tag === "pong"
    ? (tag as MatchEntry["source"])
    : "snake";
}

/**
 * Build the unified match list from both HupperVault (Pong) and HupperGames
 * (Snake & co) events for one wallet.
 *
 *   Pong:  MatchStarted ↔ MatchSettled (winnings − stake) OR
 *          MatchStarted ↔ MatchCancelled (refund → draw). Pending matches
 *          (started but unresolved) are skipped.
 *   Games: StakeLocked ↔ SessionSettled (same player, same sessionId).
 *          Payout > stake → win, == stake → draw, locked-without-settle →
 *          loss (player died, escrow swept).
 *
 * Returns most-recent-first. `userAddrLower` must already be lowercased.
 */
export function buildMatches(events: FeedEvent[], userAddrLower: string): MatchEntry[] {
  if (!userAddrLower) return [];
  const out: MatchEntry[] = [];

  // ── Pong (HupperVault) ──────────────────────────────────────
  const startedById = new Map<string, FeedEvent>();
  const settledById = new Map<string, FeedEvent>();
  const cancelledById = new Map<string, FeedEvent>();
  for (const ev of events) {
    const id = String(ev.data.matchId ?? "");
    if (!id) continue;
    if (ev.kind === "MatchStarted") startedById.set(id, ev);
    else if (ev.kind === "MatchSettled") settledById.set(id, ev);
    else if (ev.kind === "MatchCancelled") cancelledById.set(id, ev);
  }
  for (const [id, started] of startedById) {
    const stake = BigInt(String(started.data.stake ?? "0"));
    const settled = settledById.get(id);
    const cancelled = cancelledById.get(id);
    if (settled) {
      const winner = String(settled.data.winner ?? "").toLowerCase();
      const winnings = BigInt(String(settled.data.winnings ?? "0"));
      const isWinner = winner === userAddrLower;
      out.push({
        matchId: id,
        ts: settled.timestamp,
        blockNumber: settled.blockNumber,
        txHash: settled.txHash,
        outcome: isWinner ? "win" : "loss",
        pl: isWinner ? winnings - stake : -stake,
        stake,
        source: "pong",
      });
    } else if (cancelled) {
      out.push({
        matchId: id,
        ts: cancelled.timestamp,
        blockNumber: cancelled.blockNumber,
        txHash: cancelled.txHash,
        outcome: "draw",
        pl: 0n,
        stake,
        source: "pong",
      });
    }
    // else: started but unresolved — skip until on-chain resolves.
  }

  // ── Snake & co (HupperGames) ────────────────────────────────
  interface Bucket {
    locked?: FeedEvent;
    settled?: FeedEvent;
  }
  const sessions = new Map<string, Bucket>();
  for (const ev of events) {
    if (ev.kind !== "StakeLocked" && ev.kind !== "SessionSettled") continue;
    const sid = String(ev.data.sessionId ?? "");
    const player = String(ev.data.player ?? "").toLowerCase();
    if (!sid || player !== userAddrLower) continue;
    let b = sessions.get(sid);
    if (!b) {
      b = {};
      sessions.set(sid, b);
    }
    if (ev.kind === "StakeLocked") b.locked = ev;
    else if (ev.kind === "SessionSettled") b.settled = ev;
  }
  for (const [sid, { locked, settled }] of sessions) {
    if (!locked) continue; // own player never locked here
    const stake = BigInt(String(locked.data.amount ?? "0"));
    const source = gameTagToSource(settled?.data.game ?? locked.data.game);
    if (settled) {
      const payout = BigInt(String(settled.data.payout ?? "0"));
      const pl = payout - stake;
      const outcome: MatchEntry["outcome"] = pl > 0n ? "win" : pl < 0n ? "loss" : "draw";
      out.push({
        matchId: sid,
        ts: settled.timestamp,
        blockNumber: settled.blockNumber,
        txHash: settled.txHash,
        outcome,
        pl,
        stake,
        source,
      });
    } else {
      // Locked but never settled → died / forfeited.
      out.push({
        matchId: sid,
        ts: locked.timestamp,
        blockNumber: locked.blockNumber,
        txHash: locked.txHash,
        outcome: "loss",
        pl: -stake,
        stake,
        source,
      });
    }
  }

  out.sort((a, b) => b.ts - a.ts);
  return out;
}

/** Sum P&L (6-dec base units) over matches at or after `sinceMs`. */
export function netPnlSince(matches: MatchEntry[], sinceMs: number): bigint {
  let sum = 0n;
  for (const m of matches) if (m.ts >= sinceMs) sum += m.pl;
  return sum;
}
